// ── LANDING PAGE ─────────────────────────────────────────────
// Designed as an annotated document, because that is what the product does:
// it reads a grant call and marks up the parts that matter. The hero is a
// sample call with the extracted phrases highlighted, and the record NofoDesk
// builds from it — each value with the section it came from, and one
// contradiction flagged rather than silently resolved.
//
// Type: Inter throughout, the same face as the app, loaded once in index.js.
// One bold colour: the highlighter. Everything written here is something the product does today.
import { useState, useEffect } from "react";

const API = process.env.REACT_APP_API_URL || "https://api.nofodesk.com/api";
const BRAND = "#5B4FE9"; // logo colour, shared with the app's own screens

const REDUCED_MOTION = typeof window !== "undefined" &&
  window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function LogoMark({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="8" height="8" rx="2" fill={BRAND} />
      <rect x="11" y="1" width="8" height="8" rx="2" fill={BRAND} opacity="0.75" />
      <rect x="1" y="11" width="8" height="8" rx="2" fill={BRAND} opacity="0.75" />
      <rect x="11" y="11" width="8" height="8" rx="2" fill={BRAND} opacity="0.5" />
    </svg>
  );
}

const CSS = `
  body { margin: 0; background: #F3F4F0; }
  html { scroll-behavior: smooth; }
  .ld { --paper:#F3F4F0; --sheet:#FFFFFF; --ink:#16181B; --body:#3B4046; --muted:#6C727A;
        --rule:#D8DBD4; --rule-soft:#E8EAE4; --marker:#FFE066; --brand:#4B42C9; --flag:#B3261E;
        background:var(--paper); color:var(--body);
        font:16px/1.6 "Inter",system-ui,-apple-system,"Segoe UI",sans-serif;
        -webkit-font-smoothing:antialiased; }
  .ld *, .ld *::before, .ld *::after { box-sizing:border-box; }
  .ld :focus-visible { outline:2px solid var(--brand); outline-offset:2px; }
  .ld-wrap { max-width:1120px; margin:0 auto; padding:0 28px; }
  .ld-mono { font-family:"Inter",system-ui,sans-serif; }

  .ld-nav { position:sticky; top:0; z-index:50; background:rgba(243,244,240,.94);
            backdrop-filter:blur(6px); border-bottom:1px solid var(--rule); }
  .ld-nav .ld-wrap { height:60px; display:flex; align-items:center; justify-content:space-between; gap:16px; }
  .ld-brand { display:flex; align-items:center; gap:9px; font-weight:700; color:var(--ink);
              text-decoration:none; font-size:16px; letter-spacing:-.01em; }
  .ld-links { display:flex; align-items:center; gap:22px; font-size:14px; }
  .ld-links a, .ld-links button { color:var(--body); text-decoration:none; background:none; border:0;
              font:inherit; cursor:pointer; padding:0; }
  .ld-links a:hover, .ld-links button:hover { color:var(--ink); }
  @media (max-width:720px) { .ld-hide-sm { display:none; } }

  .ld-btn { display:inline-flex; align-items:center; justify-content:center; padding:12px 22px;
            border-radius:6px; background:var(--ink); color:#fff; font-weight:600; font-size:15px;
            text-decoration:none; border:1px solid var(--ink); cursor:pointer; transition:background .15s; }
  .ld-btn:hover { background:#2C2F34; }
  .ld-links .ld-btn { color:#fff; padding:8px 14px; font-size:14px; }
  .ld-btn-line { display:inline-flex; align-items:center; justify-content:center; padding:11px 20px;
                 border-radius:6px; border:1px solid var(--ink); color:var(--ink); font-weight:600;
                 font-size:15px; text-decoration:none; background:transparent; }
  .ld-btn-line:hover { background:var(--sheet); }
  .ld-textbtn { background:none; border:0; padding:12px 2px; font:inherit; font-weight:600; font-size:15px;
                color:var(--ink); cursor:pointer; text-decoration:underline; text-decoration-color:var(--marker);
                text-decoration-thickness:4px; text-underline-offset:5px; }

  .ld-hero { padding:64px 0 76px; border-bottom:1px solid var(--rule); }
  .ld-hero .ld-wrap { display:grid; grid-template-columns:minmax(0,5fr) minmax(0,6fr); gap:56px; align-items:start; }
  @media (max-width:920px) { .ld-hero .ld-wrap { grid-template-columns:1fr; gap:40px; } }
  .ld-eyebrow { font-size:12px; letter-spacing:.08em; text-transform:uppercase; color:var(--muted); }
  .ld-h1 { font-weight:800; font-size:clamp(40px,5.6vw,62px); line-height:1.02; letter-spacing:-.035em;
           color:var(--ink); margin:16px 0 22px; text-wrap:balance; }
  .ld-lede { font-size:18px; line-height:1.65; max-width:33em; margin:0 0 30px; }
  .ld-ctas { display:flex; flex-wrap:wrap; align-items:center; gap:10px 22px; }
  .ld-facts { margin-top:24px; font-size:12.5px; color:var(--muted); display:flex; flex-wrap:wrap; gap:6px 18px; }

  .ld-sheet { background:var(--sheet); border:1px solid var(--rule); border-radius:3px;
              box-shadow:0 22px 44px -28px rgba(22,24,27,.45); padding:24px 28px 22px; }
  .ld-sheet-head { display:flex; justify-content:space-between; flex-wrap:wrap; gap:4px 12px; font-size:11px;
                   color:var(--muted); border-bottom:1px solid var(--rule-soft); padding-bottom:12px; margin-bottom:16px; }
  .ld-doc { font-family:"Inter",system-ui,sans-serif; font-size:15.5px; line-height:1.72; color:#25282C; }
  .ld-doc h2 { font-family:"Inter",system-ui,sans-serif; font-size:17px; font-weight:700; color:var(--ink);
               margin:0 0 14px; letter-spacing:-.01em; line-height:1.3; }
  .ld-doc p { margin:0 0 12px; }
  .ld-sec { font-family:"Inter",system-ui,sans-serif; font-size:11px; color:var(--muted); margin-right:8px; }
  .ld-mark { background:linear-gradient(transparent 12%, var(--marker) 12%, var(--marker) 90%, transparent 90%)
             no-repeat 0 0 / 0% 100%; transition:background-size .8s cubic-bezier(.3,.7,.3,1); padding:0 1px; }
  .ld-mark.flag { --marker:#F7C6BF; }
  .is-read .ld-mark { background-size:100% 100%; }
  @media (prefers-reduced-motion:reduce) { .ld-mark { transition:none; } }

  .ld-record { margin-top:18px; border:1px solid var(--ink); border-radius:3px; background:var(--sheet); }
  .ld-record-head { display:flex; justify-content:space-between; gap:12px; padding:9px 14px; border-bottom:1px solid var(--ink);
                    font-size:11px; letter-spacing:.07em; text-transform:uppercase; color:var(--ink); }
  .ld-row { display:grid; grid-template-columns:86px minmax(0,1fr) auto; gap:12px; padding:9px 14px;
            border-bottom:1px solid var(--rule-soft); font-size:14px; align-items:baseline; }
  .ld-row:last-child { border-bottom:0; }
  .ld-key { font-family:"Inter",system-ui,sans-serif; font-size:11px; letter-spacing:.06em; color:var(--muted); }
  .ld-val { color:var(--ink); }
  .ld-loc { font-family:"Inter",system-ui,sans-serif; font-size:11px; color:var(--muted); white-space:nowrap; }
  .ld-row.flag .ld-key, .ld-row.flag .ld-val { color:var(--flag); }
  @media (max-width:540px) { .ld-row { grid-template-columns:1fr; gap:2px; } .ld-loc { white-space:normal; } }

  .ld-section { padding:84px 0; border-bottom:1px solid var(--rule); }
  .ld-h2 { font-weight:800; font-size:clamp(28px,3.5vw,40px); line-height:1.1; letter-spacing:-.025em;
           color:var(--ink); margin:0 0 14px; max-width:20em; text-wrap:balance; }
  .ld-intro { font-size:17px; max-width:38em; margin:0 0 40px; }
  .ld-defs { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); column-gap:56px; border-top:1px solid var(--ink); margin:0; }
  @media (max-width:760px) { .ld-defs { grid-template-columns:1fr; } }
  .ld-def { padding:18px 0; border-bottom:1px solid var(--rule); }
  .ld-def dt { font-weight:700; color:var(--ink); margin-bottom:4px; }
  .ld-def dd { margin:0; font-size:15px; line-height:1.6; }
  .ld-after { margin:30px 0 0; font-size:16px; max-width:42em; }
  .ld-after mark { background:linear-gradient(transparent 12%, var(--marker) 12%, var(--marker) 90%, transparent 90%); color:inherit; }

  .ld-steps { list-style:none; margin:0; padding:0; display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:40px; }
  @media (max-width:860px) { .ld-steps { grid-template-columns:1fr; gap:28px; } }
  .ld-steps li { border-top:1px solid var(--ink); padding-top:16px; }
  .ld-steps .n { font-family:"Inter",system-ui,sans-serif; font-size:12px; color:var(--muted); display:block; margin-bottom:10px; }
  .ld-steps h3 { font-size:18px; color:var(--ink); margin:0 0 6px; letter-spacing:-.01em; }
  .ld-steps p { margin:0; font-size:15px; }

  .ld-split { display:grid; grid-template-columns:minmax(0,5fr) minmax(0,7fr); gap:56px; }
  @media (max-width:860px) { .ld-split { grid-template-columns:1fr; gap:22px; } }
  .ld-list { list-style:none; margin:0; padding:0; border-top:1px solid var(--ink); }
  .ld-list li { padding:14px 0; border-bottom:1px solid var(--rule); font-size:15.5px; }
  .ld-list strong { color:var(--ink); }
  .ld-legal { margin-top:18px; font-size:14px; display:flex; flex-wrap:wrap; gap:6px 18px; }
  .ld-legal a { color:var(--ink); text-underline-offset:3px; }

  .ld-toggle { display:inline-flex; border:1px solid var(--ink); border-radius:6px; overflow:hidden; margin-bottom:26px; }
  .ld-toggle button { padding:8px 16px; background:transparent; border:0; font:inherit; font-size:14px; cursor:pointer; color:var(--ink); }
  .ld-toggle button[aria-pressed="true"] { background:var(--ink); color:#fff; }
  .ld-plans { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); border:1px solid var(--ink); border-radius:3px; background:var(--sheet); }
  @media (max-width:920px) { .ld-plans { grid-template-columns:1fr; } }
  .ld-plan { padding:26px 24px; display:flex; flex-direction:column; }
  .ld-plan + .ld-plan { border-left:1px solid var(--rule); }
  @media (max-width:920px) { .ld-plan + .ld-plan { border-left:0; border-top:1px solid var(--rule); } }
  .ld-plan.featured { background:#FFFAE3; }
  .ld-plan h3 { font-family:"Inter",system-ui,sans-serif; font-size:12px; letter-spacing:.08em; text-transform:uppercase;
                font-weight:600; color:var(--ink); margin:0 0 14px; }
  .ld-price { font-size:44px; font-weight:800; letter-spacing:-.035em; color:var(--ink); line-height:1; font-variant-numeric:tabular-nums; }
  .ld-price small { font-size:15px; font-weight:500; color:var(--muted); letter-spacing:0; }
  .ld-plan .sub { font-size:13px; color:var(--muted); margin:8px 0 18px; min-height:20px; }
  .ld-plan ul { list-style:none; margin:0 0 22px; padding:0; font-size:14.5px; flex:1; }
  .ld-plan li { padding:6px 0; border-bottom:1px solid var(--rule-soft); }
  .ld-plan .ld-btn, .ld-plan .ld-btn-line { width:100%; }

  .ld-faq { border-top:1px solid var(--ink); max-width:820px; }
  .ld-faq details { border-bottom:1px solid var(--rule); }
  .ld-faq summary { list-style:none; cursor:pointer; padding:18px 0; font-weight:600; color:var(--ink);
                    display:flex; justify-content:space-between; gap:16px; }
  .ld-faq summary::-webkit-details-marker { display:none; }
  .ld-faq summary::after { content:"+"; font-family:"Inter",system-ui,sans-serif; color:var(--muted); }
  .ld-faq details[open] summary::after { content:"\\2013"; }
  .ld-faq p { margin:0 0 18px; font-size:15.5px; max-width:44em; }

  .ld-close { padding:88px 0; border-bottom:1px solid var(--rule); }
  .ld-foot { padding:40px 0 48px; font-size:14px; color:var(--muted); }
  .ld-foot .grid { display:grid; grid-template-columns:2fr 1fr 1fr; gap:32px; margin-bottom:28px; }
  @media (max-width:720px) { .ld-foot .grid { grid-template-columns:1fr; } }
  .ld-foot h4 { font-family:"Inter",system-ui,sans-serif; font-size:11px; letter-spacing:.08em; text-transform:uppercase;
                color:var(--muted); margin:0 0 12px; font-weight:500; }
  .ld-foot a { display:block; text-decoration:none; color:var(--body); margin-bottom:8px; }
  .ld-foot a:hover { color:var(--ink); }

  .ld-modal-bg { position:fixed; inset:0; background:rgba(22,24,27,.45); display:flex; align-items:center;
                 justify-content:center; z-index:1000; padding:16px; }
  .ld-modal { background:var(--sheet); border:1px solid var(--ink); border-radius:6px; padding:28px; width:100%; max-width:380px; }
  .ld-input { width:100%; padding:10px 12px; border:1px solid var(--rule); border-radius:5px; font:inherit;
              font-size:14px; color:var(--ink); background:#fff; }
`;

// A sample call, invented for illustration. The marks are the phrases the
// extractor lifts; each record row points back to the section it came from.
const SAMPLE = [
  { sec: "C.1", parts: ["Eligible applicants are ", { m: "non-governmental organizations registered in Nigeria, Kenya or Uganda" }, " with at least three years of HIV service delivery."] },
  { sec: "B.2", parts: ["Awards will range from $1,000,000 to ", { m: "$2,500,000 per year" }, " for a period of performance of ", { m: "60 months" }, "."] },
  { sec: "D.2", parts: ["Applications must be submitted through the portal by ", { m: "30 September 2026, 17:00 CEST" }, "."] },
  { sec: "D.3", parts: ["Include ", { m: "a technical narrative of no more than 20 pages, a detailed budget with justification, and letters of support" }, "."] },
  { sec: "D.4", parts: ["Submissions received after 17:00 ", { m: "CET", flag: true }, " on the closing date will not be reviewed."] },
];
const RECORD = [
  ["DEADLINE", "30 Sep 2026, 17:00", "§ D.2"],
  ["CONFLICT", "Timezone given as CEST in § D.2, CET in § D.4", "check", true],
  ["CEILING", "$2,500,000 per year", "§ B.2"],
  ["PERIOD", "60 months", "§ B.2"],
  ["ELIGIBLE", "NGOs registered in Nigeria, Kenya or Uganda; 3+ years of HIV services", "§ C.1"],
  ["SUBMIT", "Narrative (≤ 20 pp) · Budget and justification · Letters of support", "§ D.3"],
];

function SampleCall({ read }) {
  let n = 0;
  return (
    <div id="sample-call">
      <div className={`ld-sheet${read ? " is-read" : ""}`}>
        <div className="ld-sheet-head ld-mono">
          <span>Notice of Funding Opportunity GH-26-014</span>
          <span>Sample call · for illustration</span>
        </div>
        <div className="ld-doc">
          <h2>Strengthening Community HIV Testing Services</h2>
          {SAMPLE.map(({ sec, parts }) => (
            <p key={sec}>
              <span className="ld-sec">§ {sec}</span>
              {parts.map((p, i) => typeof p === "string"
                ? <span key={i}>{p}</span>
                : <span key={i} className={`ld-mark${p.flag ? " flag" : ""}`} style={{ transitionDelay: `${(n++) * 260}ms` }}>{p.m}</span>)}
            </p>
          ))}
        </div>
      </div>
      <div className="ld-record" aria-label="The record NofoDesk builds from this call">
        <div className="ld-record-head ld-mono"><span>Extracted record</span><span>Where found</span></div>
        {RECORD.map(([key, val, loc, flag]) => (
          <div key={key} className={`ld-row${flag ? " flag" : ""}`}>
            <span className="ld-key">{key}</span>
            <span className="ld-val">{val}</span>
            <span className="ld-loc">{loc}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmailModal({ onClose, onSuccess }) {
  const [isSignup, setIsSignup] = useState(false);
  const [form,     setForm]     = useState({ name: "", email: "", password: "" });
  const [err,      setErr]      = useState("");
  const [loading,  setLoading]  = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const endpoint = isSignup ? "auth/email-signup" : "auth/email-login";
      const body = isSignup ? { name: form.name, email: form.email, password: form.password } : { email: form.email, password: form.password };
      // credentials: the session comes back as an httpOnly cookie.
      const res = await fetch(`${API}/${endpoint}`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" }, body: JSON.stringify(body) });
      const d   = await res.json();
      if (!res.ok) { setErr(d.error || "Something went wrong"); return; }
      onSuccess(d.user);
    } catch { setErr("Network error. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div className="ld-modal-bg" role="dialog" aria-modal="true" aria-label={isSignup ? "Create your account" : "Sign in"}>
      <div className="ld-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <LogoMark size={20} />
            <span style={{ fontWeight: 700, color: "var(--ink)" }}>NofoDesk</span>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 22, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)", marginBottom: 4 }}>{isSignup ? "Create your account" : "Sign in"}</div>
        <div style={{ fontSize: 14, color: "var(--muted)", marginBottom: 20 }}>{isSignup ? "Start with 5 free extractions a month." : "Welcome back."}</div>

        <a href={`${API}/auth/google-login`} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "10px 16px", background: "#fff", border: "1px solid var(--rule)", borderRadius: 6, textDecoration: "none", marginBottom: 10 }}>
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.08 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-3.58-13.46-8.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#333" }}>Continue with Google</span>
        </a>
        <a href={`${API}/auth/microsoft-login`} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "10px 16px", background: "#fff", border: "1px solid var(--rule)", borderRadius: 6, textDecoration: "none", marginBottom: 16 }}>
          <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true"><rect x="1" y="1" width="9" height="9" fill="#F25022"/><rect x="11" y="1" width="9" height="9" fill="#7FBA00"/><rect x="1" y="11" width="9" height="9" fill="#00A4EF"/><rect x="11" y="11" width="9" height="9" fill="#FFB900"/></svg>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#333" }}>Continue with Microsoft</span>
        </a>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <div style={{ flex: 1, height: 1, background: "var(--rule)" }} />
          <span style={{ fontSize: 12, color: "var(--muted)" }}>or use email</span>
          <div style={{ flex: 1, height: 1, background: "var(--rule)" }} />
        </div>

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {isSignup && <input className="ld-input" placeholder="Your name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />}
          <input className="ld-input" type="email" placeholder="Email address" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
          <input className="ld-input" type="password" placeholder={isSignup ? "Password (at least 8 characters)" : "Password"} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required />
          {err && <div role="alert" style={{ fontSize: 13, color: "var(--flag)", background: "#FDF1EF", border: "1px solid #F2C9C3", borderRadius: 5, padding: "8px 12px" }}>{err}</div>}
          <button type="submit" className="ld-btn" disabled={loading}>{loading ? "Please wait…" : isSignup ? "Create account" : "Sign in"}</button>
        </form>
        <div style={{ textAlign: "center", marginTop: 16, fontSize: 13, color: "var(--muted)" }}>
          {isSignup ? "Already have an account? " : "No account yet? "}
          <button onClick={() => { setIsSignup(s => !s); setErr(""); }} style={{ background: "none", border: "none", color: "var(--ink)", fontWeight: 600, cursor: "pointer", fontSize: 13, textDecoration: "underline", textUnderlineOffset: 3 }}>
            {isSignup ? "Sign in" : "Create one free"}
          </button>
        </div>
      </div>
    </div>
  );
}

const WHAT_IT_READS = [
  ["Every date", "Letter of intent, full application, questions deadline, award and start dates — with the timezone as the call states it."],
  ["The money", "Award floor and ceiling, total funding, number of awards, cost share, and any cap on indirect costs."],
  ["Who can apply", "Organisation types, where applicants must be registered, required registrations such as SAM or UEI, and minimum experience."],
  ["What to submit", "Every required document and attachment, page limits by section, and how and where to submit."],
  ["How it is scored", "Pass/fail criteria and the technical criteria with their weights."],
  ["Who to ask", "Contacts, the application link and every official link in the call."],
];

const BEYOND = [
  ["Deadline tracker", "Countdowns for every saved deadline, with email alerts 90, 60, 30, 7, 3 and 1 day out."],
  ["Applications pipeline", "Every application with its status and amount, in a dashboard and a calendar."],
  ["Eligibility check", "Compares a call with your organisation profile and explains the result."],
  ["Proposal drafting", "Drafts sections from the call and your profile. Facts it doesn't have are left as marked placeholders, not invented."],
  ["Budgets", "Builds a budget within the funder's cost rules, drafts the justification, and fills in the funder's own Excel template."],
  ["Grant discovery", "Search open opportunities and extract any of them in one step."],
  ["Team workspaces", "One shared pipeline with owner, editor, viewer and visitor access."],
  ["Exports and storage", "Excel and PowerPoint exports; each grant's documents kept in Google Drive or OneDrive."],
];

const FAQS = [
  ["Does it work with my funder?", "Yes. It reads calls from any funder — government, foundation or multilateral — and returns the same record every time, so calls from different funders can be compared side by side."],
  ["How accurate is it?", "You review every field before anything is saved, and for the key fields you see the sentence each value came from. Accuracy is measured from the corrections people make at that step. When a call contradicts itself, you get both values rather than a guess."],
  ["What if a field is wrong or missing?", "Edit it before saving, or have the call read again with the focus on budgets, eligibility, evaluation criteria or dates."],
  ["Is my data safe?", "Documents go to the AI under paid terms that forbid training on them, and NofoDesk never trains a model on customer content. Uploaded files are processed in memory and not stored. You can download or delete everything at any time."],
  ["Can my whole team use it?", "Yes. Team plans share one pipeline, tracker and calendar, with owner, editor, viewer and visitor access. Team and Enterprise are quoted per organisation, with contracts and invoices — write to info@nofodesk.com."],
  ["Does it work for organisations in Nigeria, Ghana or Kenya?", "Yes. Pricing is set for organisations in Africa, and you can pay in NGN, GHS, KES, USD or EUR through Flutterwave."],
  ["Do I need technical skills?", "No. If you can paste a link or upload a PDF, you can use it. There is nothing to set up."],
];

export default function LandingPage({ onAuthSuccess }) {
  const loginUrl = `${API}/auth/google-login`;
  const [emailModal, setEmailModal] = useState(false);
  const [billing, setBilling] = useState("monthly");
  const [read, setRead] = useState(REDUCED_MOTION);

  // The highlighter sweeps once after load, and again on request.
  useEffect(() => {
    if (REDUCED_MOTION) return undefined;
    const t = setTimeout(() => setRead(true), 450);
    return () => clearTimeout(t);
  }, []);
  const readAgain = () => {
    const el = document.getElementById("sample-call");
    if (el) el.scrollIntoView({ behavior: REDUCED_MOTION ? "auto" : "smooth", block: "center" });
    if (REDUCED_MOTION) return;
    setRead(false);
    setTimeout(() => setRead(true), 380);
  };

  const annual = billing === "annual";

  return (
    <div className="ld">
      <style>{CSS}</style>

      <header className="ld-nav">
        <div className="ld-wrap">
          <a className="ld-brand" href="#top"><LogoMark size={22} /> NofoDesk</a>
          <nav className="ld-links" aria-label="Main">
            <a className="ld-hide-sm" href="#features">Features</a>
            <a className="ld-hide-sm" href="#pricing">Pricing</a>
            <button onClick={() => setEmailModal(true)}>Sign in</button>
            <a className="ld-btn" href={loginUrl}>Start free</a>
          </nav>
        </div>
      </header>

      <main id="top">
        <section className="ld-hero">
          <div className="ld-wrap">
            <div>
              <div className="ld-eyebrow ld-mono">For grants teams</div>
              <h1 className="ld-h1">The grant call, read for you.</h1>
              <p className="ld-lede">
                Paste a link or upload a PDF. NofoDesk pulls the deadline, eligibility, award ceiling and every
                required document into one record, and shows you where in the call each one came from.
              </p>
              <div className="ld-ctas">
                <a className="ld-btn" href={loginUrl}>Start free</a>
                <button className="ld-textbtn" onClick={readAgain}>Watch it read a sample call</button>
              </div>
              <div className="ld-facts ld-mono">
                <span>5 free extractions a month</span>
                <span>No card needed</span>
                <span>Your documents never train AI</span>
              </div>
            </div>
            <SampleCall read={read} />
          </div>
        </section>

        <section className="ld-section" id="features">
          <div className="ld-wrap">
            <h2 className="ld-h2">What it pulls out of a call</h2>
            <p className="ld-intro">The same record for every call, whoever the funder is, so nothing depends on finding the right page.</p>
            <dl className="ld-defs">
              {WHAT_IT_READS.map(([t, d]) => (
                <div key={t} className="ld-def"><dt>{t}</dt><dd>{d}</dd></div>
              ))}
            </dl>
            <p className="ld-after">
              When a call contradicts itself, <mark>you see both values and where each appears</mark>. When a document is
              too long to read in full, the record says which part was skipped.
            </p>
          </div>
        </section>

        <section className="ld-section">
          <div className="ld-wrap">
            <h2 className="ld-h2">How it works</h2>
            <p className="ld-intro">Three steps, and a person approves the result before it is saved.</p>
            <ol className="ld-steps">
              <li><span className="n">1</span><h3>Add the call</h3><p>Paste a link — Grants.gov, a funder's site — or upload the PDF or Word file from your inbox.</p></li>
              <li><span className="n">2</span><h3>Check the record</h3><p>Every field is editable. Nothing reaches your tracker until you confirm it.</p></li>
              <li><span className="n">3</span><h3>Put it to work</h3><p>Track the deadline, check eligibility, draft a proposal section or build the budget.</p></li>
            </ol>
          </div>
        </section>

        <section className="ld-section">
          <div className="ld-wrap">
            <h2 className="ld-h2">After the call is read</h2>
            <p className="ld-intro">The rest of a grants team's week, in the same place.</p>
            <dl className="ld-defs">
              {BEYOND.map(([t, d]) => (
                <div key={t} className="ld-def"><dt>{t}</dt><dd>{d}</dd></div>
              ))}
            </dl>
          </div>
        </section>

        <section className="ld-section">
          <div className="ld-wrap ld-split">
            <div>
              <h2 className="ld-h2">Your documents stay yours</h2>
              <p style={{ margin: 0, fontSize: 16 }}>What happens to a grant call or budget after you upload it.</p>
            </div>
            <div>
              <ul className="ld-list">
                <li><strong>No training on your content.</strong> The AI runs under paid terms that forbid it, and NofoDesk never trains or fine-tunes a model on customer documents.</li>
                <li><strong>Uploads are not stored.</strong> Files are processed in memory; only the record you confirm is saved.</li>
                <li><strong>A person decides.</strong> Every extraction is reviewed before it is saved, and budget figures are calculated by the software, never by the AI.</li>
                <li><strong>Your data, on request.</strong> Download everything held for your account, or delete it, at any time.</li>
              </ul>
              <div className="ld-legal">
                <a href="/legal/subprocessors.html">Sub-processors</a>
                <a href="/legal/data-retention.html">Data retention</a>
                <a href="/legal/ai-use-policy.html">AI use policy</a>
                <a href="/legal/security.html">Security answers</a>
                <a href="/grant-management-software">For US nonprofits</a>
              </div>
            </div>
          </div>
        </section>

        <section className="ld-section" id="pricing">
          <div className="ld-wrap">
            <h2 className="ld-h2">Pricing</h2>
            <p className="ld-intro">Start free. Pay monthly or yearly, in your own currency.</p>
            <div className="ld-toggle" role="group" aria-label="Billing period">
              <button aria-pressed={!annual} onClick={() => setBilling("monthly")}>Monthly</button>
              <button aria-pressed={annual} onClick={() => setBilling("annual")}>Yearly · 20% off</button>
            </div>
            <div className="ld-plans">
              <div className="ld-plan">
                <h3>Free</h3>
                <div className="ld-price">$0 <small>/ month</small></div>
                <div className="sub">No card needed</div>
                <ul>
                  <li>5 extractions a month</li>
                  <li>Deadline tracking</li>
                  <li>Browse grant discovery</li>
                </ul>
                <a className="ld-btn-line" href={loginUrl}>Start free</a>
              </div>
              <div className="ld-plan featured">
                <h3>Professional</h3>
                <div className="ld-price">${annual ? "39" : "49"} <small>/ month</small></div>
                <div className="sub">{annual ? "Billed $468 a year" : "Billed monthly · cancel anytime"}</div>
                <ul>
                  <li>50 extractions a month</li>
                  <li>Proposal drafting</li>
                  <li>Budgets, justifications and funder templates</li>
                  <li>Eligibility check</li>
                  <li>Full grant discovery</li>
                  <li>Email deadline alerts</li>
                  <li>Applications pipeline and calendar</li>
                  <li>Excel and PowerPoint export</li>
                  <li>Google Drive and OneDrive</li>
                </ul>
                <a className="ld-btn" href={loginUrl}>Start free, upgrade in the app</a>
              </div>
              <div className="ld-plan">
                <h3>Team &amp; Enterprise</h3>
                <div className="ld-price">Custom</div>
                <div className="sub">Quoted per organisation · from 5 seats</div>
                <ul>
                  <li>Everything in Professional</li>
                  <li>Unlimited extractions</li>
                  <li>Shared workspaces with roles</li>
                  <li>Annual contracts and invoices</li>
                  <li>Data processing agreement</li>
                  <li>Onboarding and support</li>
                </ul>
                <a className="ld-btn-line" href={"mailto:info@nofodesk.com?subject=" + encodeURIComponent("NofoDesk Team / Enterprise")}>Ask for a quote</a>
              </div>
            </div>
          </div>
        </section>

        <section className="ld-section">
          <div className="ld-wrap">
            <h2 className="ld-h2">Questions</h2>
            <div className="ld-faq">
              {FAQS.map(([q, a]) => (
                <details key={q}><summary>{q}</summary><p>{a}</p></details>
              ))}
            </div>
          </div>
        </section>

        <section className="ld-close">
          <div className="ld-wrap">
            <h2 className="ld-h2">Start with the call on your desk.</h2>
            <p className="ld-intro" style={{ marginBottom: 26 }}>Five free extractions every month. No card needed.</p>
            <a className="ld-btn" href={loginUrl}>Start free</a>
          </div>
        </section>
      </main>

      <footer className="ld-foot">
        <div className="ld-wrap">
          <div className="grid">
            <div>
              <div className="ld-brand" style={{ marginBottom: 10 }}><LogoMark size={20} /> NofoDesk</div>
              <p style={{ margin: "0 0 8px" }}>Grant calls, budgets and deadlines in one place.</p>
              <a href="mailto:info@nofodesk.com" className="ld-mono">info@nofodesk.com</a>
            </div>
            <div>
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href={loginUrl}>Start free</a>
            </div>
            <div>
              <h4>Legal</h4>
              <a href="/privacy.html">Privacy policy</a>
              <a href="/legal/subprocessors.html">Sub-processors</a>
              <a href="/legal/data-retention.html">Data retention</a>
              <a href="/legal/ai-use-policy.html">AI use policy</a>
            </div>
          </div>
          <div style={{ borderTop: "1px solid var(--rule)", paddingTop: 18 }}>© {new Date().getFullYear()} FOKT Consulting LLC. NofoDesk is a product of <a href="https://foktconsulting.com" target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}>FOKT Consulting LLC</a>.</div>
        </div>
      </footer>

      {emailModal && (
        <EmailModal
          onClose={() => setEmailModal(false)}
          onSuccess={(user) => { setEmailModal(false); if (onAuthSuccess) onAuthSuccess(user); }}
        />
      )}
    </div>
  );
}
